import { supabase } from "../lib/supabase";
import { fetchBookingStorageBlobViaEdge } from "./bookingStorageFileViaEdge";

async function loadCommercialInvoiceBlob(
  cleanPath: string,
  bookingId?: number
): Promise<Blob> {
  if (bookingId != null && Number.isFinite(bookingId)) {
    return fetchBookingStorageBlobViaEdge({
      bucket: "commercial_invoices",
      objectPath: cleanPath,
      bookingId,
    });
  }

  const { data, error } = await supabase.storage
    .from("commercial_invoices")
    .download(cleanPath);

  if (error || !data) {
    throw error ?? new Error("Could not download file");
  }
  return data;
}

/** Download a commercial invoice blob (for Excel preview / parsing). */
export const fetchCommercialInvoiceBlob = loadCommercialInvoiceBlob;

/**
 * Sanitize filename to prevent special characters and spaces
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return `file_${Date.now()}`; // Handle undefined or null file names
  
  return fileName
    .replace(/\s+/g, "_") // Replace spaces with underscores
    .replace(/[^\w.-]/g, "") // Remove any non-alphanumeric characters except dots and hyphens
    .normalize("NFD") // Normalize accented characters
    .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics
};

/**
 * Generate a file path for commercial invoice using company ID as the root folder
 */
export const generateCompanyBasedPath = (
  companyId: number | string,
  bookingId: number | string,
  fileName: string
): string => {
  // Ensure we have valid values for all parameters
  const safeCompanyId = companyId || 0;
  const safeBookingId = bookingId || 0;
  
  // Sanitize the filename
  const sanitizedFileName = sanitizeFileName(fileName);
  
  // Create path with company_id as root
  return `${safeCompanyId}/${safeBookingId}/${Date.now()}_${sanitizedFileName}`;
};

/**
 * Upload a commercial invoice file using the company-based path structure
 */
export const uploadCommercialInvoice = async (
  bookingId: number | string,
  file: File, 
  appendExisting: boolean = false,
  companyId: number | string = 0
): Promise<{ error: unknown; path?: string } | null> => {
  try {
    if (!file) {
      console.error("No file provided for upload");
      return { error: "No file provided" };
    }
    
    console.log("uploadCommercialInvoice starting with:", {
      bookingId,
      companyId,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      appendExisting
    });
    
    // Get company ID from booking if not provided
    const finalCompanyId = companyId || 0; // Use 0 as default company ID if not provided
    
    // Generate path with company ID as root
    const filePath = generateCompanyBasedPath(finalCompanyId, bookingId, file.name);
    
    console.log("Generated file path:", filePath);
    
    // Upload to Supabase storage
    console.log("Uploading to bucket:", "commercial_invoices");
    const { data, error } = await supabase.storage
      .from("commercial_invoices")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });
    
    if (error) {
      console.error("Error uploading commercial invoice:", error);
      return { error };
    }
    
    console.log("Commercial invoice uploaded successfully:", data.path);
    return { error: null, path: data.path };
  } catch (error) {
    console.error("Error in uploadCommercialInvoice:", error);
    return { error };
  }
};

/**
 * Get public URL for a commercial invoice file
 */
export const getCommercialInvoicePublicUrl = (
  path: string
): string | null => {
  try {
    if (!path || path.trim() === '') {
      return null;
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    const { data } = supabase.storage
      .from("commercial_invoices")
      .getPublicUrl(cleanPath);
    
    return data.publicUrl;
  } catch (error) {
    console.error("Error getting public URL:", error);
    return null;
  }
};

/**
 * Get public URLs for multiple commercial invoice files
 */
export const getCommercialInvoicePublicUrls = (
  pathsString: string
): string[] => {
  if (!pathsString || pathsString.trim() === '') {
    return [];
  }
  
  // Split by comma and process each path
  const paths = pathsString.split(",");
  return paths.map(path => {
    const url = getCommercialInvoicePublicUrl(path);
    return url || "";
  }).filter(url => url !== "");
};

/**
 * Preview URL (blob:) for a commercial invoice. Prefer passing `bookingId` so hosted
 * Storage is accessed via the Edge Function (service-role download).
 *
 * @param expiresIn Kept for API compatibility.
 */
export const createCommercialInvoiceSignedUrl = async (
  path: string,
  expiresIn: number = 3600,
  bookingId?: number
): Promise<string | null> => {
  void expiresIn;
  try {
    if (!path || path.trim() === '') {
      return null;
    }

    const cleanPath = path.trim().replace(/^\//, "");

    const blob = await loadCommercialInvoiceBlob(cleanPath, bookingId);
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error in createCommercialInvoiceSignedUrl:", error);
    return null;
  }
};

/**
 * Create signed URLs for multiple commercial invoice files
 */
export const createCommercialInvoiceSignedUrls = async (
  pathsString: string,
  expiresIn: number = 3600,
  bookingId?: number
): Promise<(string | null)[]> => {
  if (!pathsString || pathsString.trim() === '') {
    return [];
  }
  
  const paths = pathsString
    .split(",")
    .map((p) => p.trim().replace(/^\//, ""))
    .filter(Boolean);

  if (paths.length === 0) {
    return [];
  }

  void expiresIn;
  const results = await Promise.all(
    paths.map(async (p) => {
      try {
        const blob = await loadCommercialInvoiceBlob(p, bookingId);
        return URL.createObjectURL(blob);
      } catch (e) {
        console.error("Error downloading commercial invoice:", e);
        return null;
      }
    })
  );
  return results;
};

/**
 * Download a commercial invoice file
 */
export const downloadCommercialInvoice = async (
  path: string,
  filename: string,
  bookingId?: number
): Promise<void> => {
  try {
    if (!path || path.trim() === '') {
      throw new Error("Invalid file path provided");
    }
    
    // Clean the file path - remove any leading slash
    const cleanPath = path.trim().replace(/^\//, "");
    
    console.log("Downloading commercial invoice:", cleanPath);
    
    const fileExt = cleanPath.split(".").pop()?.toLowerCase() || "pdf";

    const blob = await loadCommercialInvoiceBlob(cleanPath, bookingId);

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${filename}.${fileExt}`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    console.log("Download completed for:", filename);
  } catch (error) {
    console.error("Error downloading commercial invoice:", error);
    throw error;
  }
}; 